import React, { useMemo, useCallback } from 'react';
import {
  useSysAdminUserDelete,
  useSysAdminUserGetFilters,
  useSysAdminUserList,
} from '../hooks/use-sysadmin-user.hook';
import type { ColumnConfig } from '../../../../common/PaginatedTable';
import type { SysAdminUser } from '../types/sysadmin-user.types';
import SysAdminUserTable from '../components/sysadmin-user-table.component';
import { DEFAULT_PAGINATED_TABLE_DATA } from '../../../../../const/default-paginated-table-data.const';
import ActionButtons from '../../../../common/ActionButtons';
import { useFilters } from '../../../../../hooks/use-filters.hook';
import { message, Tag, Avatar } from 'antd';
import HeaderRow from '../../../../common/HeaderRow';
import { useTranslation } from 'react-i18next';
import { formatDate } from '@utils/date.util';
import { useAuthUser } from '../../../../../states/user.atom';
import { rbac } from 'permissions/permissions';
import { WithSysAdminPermissionCheck } from '../../../../auth/WithSysAdminPermissionCheck';
import { extractCreateError } from '@services/extract-api-error.service';

const SysAdminUserTableContainer = () => {
  const { t } = useTranslation();
  const { getUserPermissions } = useAuthUser();
  const userPermissions = getUserPermissions();

  const {
    queryParams,
    handlePageChange,
    handlePageSizeChange,
    handleFilterChange,
    handleSearchChange,
    handleSortChange,
  } = useFilters();

  const { data: availableFilters, isLoading: isAvailableFiltersLoading } =
    useSysAdminUserGetFilters();

  const { data, isLoading, isFetching } = useSysAdminUserList(queryParams);
  const { docs: tableData = [] } = data ?? DEFAULT_PAGINATED_TABLE_DATA;

  const deleteUserMutation = useSysAdminUserDelete();

  const handleDeleteUser = useCallback(async (record: SysAdminUser) => {
    try {
      await deleteUserMutation.mutateAsync(record.id);
      message.success(t('crud.delete.success', { entity: t('users.title') }));
    } catch (error) {
      const errorMessage = extractCreateError(
        error,
        t('crud.delete.error', { entity: t('users.title') })
      );
      message.error(errorMessage);
    }
  }, []);

  // Handle table sorting
  const handleTableChange = useCallback(
    (pagination: any, filters: any, sorter: any) => {
      if (sorter && sorter.field) {
        // Convert antd sorter.order to our expected sortOrder format
        let sortOrder: 'asc' | 'desc';

        if (sorter.order === 'ascend') {
          sortOrder = 'asc';
        } else if (sorter.order === 'descend') {
          sortOrder = 'desc';
        } else {
          // When clicking the third time (cancel sort), cycle back to ascending
          sortOrder = 'asc';
        }

        handleSortChange(sorter.field as string, sortOrder);
      } else {
        handleSortChange('createdAt', 'desc');
      }
    },
    [handleSortChange]
  );

  const columns = useMemo<ColumnConfig<SysAdminUser>[]>(
    () => [
      {
        title: t('users.name.label'),
        key: 'name',
        dataIndex: 'name',
        sorter: true,
      },
      {
        title: t('users.email.label'),
        key: 'email',
        dataIndex: 'email',
        sorter: true,
      },
      {
        title: t('users.lastLoginAt.label'),
        key: 'lastLoginAt',
        dataIndex: 'lastLoginAt',
        render: (lastLoginAt) => <span>{lastLoginAt ? formatDate(lastLoginAt) : '-'}</span>,
        sorter: true,
      },
      {
        title: t('users.emailVerified.label'),
        key: 'emailVerified',
        dataIndex: 'emailVerified',
        render: (emailVerified: boolean) =>
          emailVerified ? (
            <Tag color="green">{t('common.yes')}</Tag>
          ) : (
            <Tag color="red">{t('common.no')}</Tag>
          ),
      },
      {
        title: t('users.picture.label'),
        key: 'picture',
        dataIndex: 'picture',
        render: (picture: string, record: SysAdminUser) => {
          return (
            <Avatar src={picture} alt={t('users.picture.label')}>
              {!picture && record.name[0]}
            </Avatar>
          );
        },
      },
      {
        title: 'Actions',
        key: 'actions',
        align: 'right',
        render: (_: any, record: SysAdminUser) => (
          <ActionButtons
            showEdit={rbac.hasAccess(userPermissions, 'user.update')}
            showDelete={rbac.hasAccess(userPermissions, 'user.delete')}
            showView={rbac.hasAccess(userPermissions, 'user.view')}
            record={record}
            baseUrl="/dashboard/admin/user"
            onDelete={handleDeleteUser}
            isDeleting={deleteUserMutation.isPending && deleteUserMutation.variables === record.id}
          />
        ),
      },
    ],
    [handleDeleteUser, deleteUserMutation.isPending, deleteUserMutation.variables]
  );

  return (
    <>
      <HeaderRow
        isSysAdmin={true}
        title={t('crud.list.title', { entity: t('users.title') })}
        createRoute="create"
        showCreateButton={rbac.hasAccess(userPermissions, 'user.create')}
      />
      <div className="row mt-3">
        <SysAdminUserTable
          data={tableData}
          columns={columns}
          loading={
            isLoading || deleteUserMutation.isPending || isFetching || isAvailableFiltersLoading
          }
          pagination={data}
          rowKey="id"
          showSizeChanger={true}
          onPageChange={handlePageChange}
          onPageSizeChange={handlePageSizeChange}
          showSearchBar={true}
          onSearchChange={handleSearchChange}
          showFilters={true}
          availableFilters={availableFilters?.data}
          onFilterChange={handleFilterChange}
          tableProps={{
            onChange: handleTableChange,
            sortDirections: ['ascend', 'descend'],
          }}
          sortField={queryParams.sortField}
          sortOrder={queryParams.sortOrder}
        />
      </div>
    </>
  );
};

export default WithSysAdminPermissionCheck(SysAdminUserTableContainer, 'user.view');
